import { ClientLiveOperation, ClientMiraAllClients, NotLiveReason } from "@/data/models/ClientAdministrationRow";

/**
 * "Kein Echtbetrieb" (ticket 2610043) as the IRES Admin Client shows and edits it. Pure; the callers
 * pass the translator and the date formatting. The marker itself and its rules: IRES Cloud
 * `docs/apis/list-clients-for-administration.md`.
 */

type Translate = (key: string, values?: unknown[]) => string;

const PREFIX = "admin.clients.liveOperation";

/** A reason's label: Test, Demo, Lager. */
export function notLiveReasonLabel(reason: NotLiveReason, t: Translate): string {
  return t(`${PREFIX}.reasons.${reason}`);
}

/**
 * The detail's value: "Echtbetrieb", or "Kein Echtbetrieb (Lager)". A client never marked, or served by
 * an IRES Cloud without the marker, is live operation.
 */
export function liveOperationValue(liveOperation: ClientLiveOperation | null | undefined, t: Translate): string {
  if (!liveOperation || liveOperation.live) {
    return t(`${PREFIX}.live`);
  }

  return liveOperation.reason
    ? t(`${PREFIX}.notLiveWithReason`, [notLiveReasonLabel(liveOperation.reason, t)])
    : t(`${PREFIX}.notLive`);
}

/**
 * The last change under the value: "geändert am 13.10.2026 von Max Muster", without "von" when the
 * name is not recorded; null when the client was never marked or the date is not recorded. Used for
 * "Mira: Alle Mandanten" too (#2610111), which records its last change the same way.
 */
export function liveOperationChangeText(
  liveOperation: ClientLiveOperation | ClientMiraAllClients | null | undefined,
  formatDate: (date: Date) => string,
  t: Translate
): string | null {
  if (!liveOperation || liveOperation.changedAtMillis === null) {
    return null;
  }

  const date = formatDate(new Date(liveOperation.changedAtMillis));
  return liveOperation.changedByName
    ? t(`${PREFIX}.changedAtBy`, [date, liveOperation.changedByName])
    : t(`${PREFIX}.changedAt`, [date]);
}

/**
 * Whether the dialog's choice differs from what is stored. Changing only the reason of a client that
 * stays "Kein Echtbetrieb" is a change too, and so is choosing "Kein Echtbetrieb" for a live client
 * before a reason is picked (ABBRECHEN must ask).
 *
 * @param initialReason - the stored reason, null for live operation.
 * @param live - the dialog's choice.
 * @param reason - the dialog's reason (ignored when live).
 */
export function hasLiveOperationChanged(initialReason: NotLiveReason | null, live: boolean, reason: NotLiveReason | null): boolean {
  return live ? initialReason !== null : initialReason === null || reason !== initialReason;
}

/** Whether the dialog may save: something changed, and "Kein Echtbetrieb" has its reason. */
export function canSaveLiveOperation(initialReason: NotLiveReason | null, live: boolean, reason: NotLiveReason | null): boolean {
  return hasLiveOperationChanged(initialReason, live, reason) && (live || reason !== null);
}
